import type {
  ConflictDocument,
  DestinoDocumentoPadraoNoCadastro,
  DestinoDoDocumentoPadraoNaApi,
  ProofDocumentRequirement,
  SubtipoDoComprovante,
} from '../types/documentsTypes';

/**
 * O comprovante ainda aceita um arquivo: sem arquivo concluído e sem envio, processamento ou
 * remoção em curso. É a mesma condição que mostra o upload no `ProofDocumentBlock` e que oferece a
 * busca na biblioteca — com duas regras, a busca aparecia ao lado da barra de progresso do mesmo
 * requisito.
 *
 * @example comprovanteAceitaArquivo({ ...requisito, status: 'idle', fileId: null }) // true
 */
export function comprovanteAceitaArquivo(comprovante: ProofDocumentRequirement): boolean {
  const temArquivo = Boolean(comprovante.fileId) && comprovante.status === 'done';
  const emCurso =
    comprovante.status === 'uploading' ||
    comprovante.status === 'processing' ||
    Boolean(comprovante.isRemoving);
  return !temArquivo && !emCurso;
}

/** O destino como a pessoa o lê no painel. O do comprovante já traz a parte e o lado. */
export function rotuloDoDestinoDocumentoPadrao(destino: DestinoDocumentoPadraoNoCadastro): string {
  switch (destino.tipo) {
    case 'PETICAO_INICIAL':
      return 'Petição inicial';
    case 'ANEXO_CONFLITO':
      return 'Documentos do conflito';
    case 'COMPROVANTE':
      return destino.requirementLabel;
  }
}

/**
 * O subtipo que o core grava para o comprovante, o mesmo no upload local e no documento padrão.
 *
 * @example subtipoDoComprovante('carteiraOab') // 'CARTEIRA_OAB'
 */
export function subtipoDoComprovante(
  documentType: ProofDocumentRequirement['documentType'],
): SubtipoDoComprovante {
  if (documentType === 'procuracao') return 'PROCURACAO';
  if (documentType === 'carteiraOab') return 'CARTEIRA_OAB';
  return 'CONTRATO_SOCIAL';
}

/**
 * O destino como o core o recebe. O do comprovante leva o subtipo e a parte do requisito ATUAL na
 * etapa, como o upload local, para o documento reaparecer no mesmo requisito quando a etapa
 * recarregar. `null`: o requisito saiu da etapa (a parte foi tirada) com o painel aberto.
 *
 * @example destinoNoRascunhoParaApi({ tipo: 'ANEXO_CONFLITO' }, comprovantes) // { tipo: 'ANEXO_CONFLITO' }
 */
export function destinoNoRascunhoParaApi(
  destino: DestinoDocumentoPadraoNoCadastro,
  comprovantes: ProofDocumentRequirement[],
): DestinoDoDocumentoPadraoNaApi | null {
  if (destino.tipo !== 'COMPROVANTE') return { tipo: destino.tipo };
  const requisito = comprovantes.find((item) => item.id === destino.requirementId);
  if (!requisito) return null;
  return {
    tipo: 'COMPROVANTE',
    requirementId: requisito.id,
    subtipo: subtipoDoComprovante(requisito.documentType),
    partyId: requisito.partyId,
    partyName: requisito.partyName,
    partyRole: requisito.partyRole,
    representedSide: requisito.representedSide,
  };
}

/** Nomes dos arquivos já presentes na etapa, para o aviso de nome repetido do painel. */
export function nomesDosArquivosDoCadastro(
  comprovantes: ProofDocumentRequirement[],
  documentosDoConflito: ConflictDocument[],
): string[] {
  return [
    ...comprovantes.map((comprovante) => comprovante.fileName),
    ...documentosDoConflito.map((documento) => documento.fileName),
  ].filter((nome): nome is string => Boolean(nome));
}
